import * as React from "react";
import { useContext, useState, useCallback, useEffect } from "react";

import { Button, Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MobileStepper from "@mui/material/MobileStepper";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";

import SwipeableViews from "react-swipeable-views";
import { autoPlay } from "react-swipeable-views-utils";

import CarouselPaper from "./CarouselPaper";

import { Service, ServiceDescription } from "../../../../types/entities";
import { LangContext } from "../../../../types/internationalization";
import {
	BusinessDataContext,
	BusinessDataContextValues,
	MultistepContext,
	MultistepContextValues,
} from "../../../../types/context";
import { generateTranslations } from "../../../../utils/serviceTranslations";

import LoadingTranslations from "./LoadingTranslations";

const AutoPlaySwipeableViews = autoPlay(SwipeableViews);

export const ServicesCarousel: React.FC<{ filteredServices: Service[] }> = ({
	filteredServices,
}) => {
	const { onNext, onChangeService } = useContext(
		MultistepContext
	) as MultistepContextValues;

	const { services } = useContext(
		BusinessDataContext
	) as BusinessDataContextValues;

	const mappedServices = services.map((s) => ({
		id: s.id,
		name: s.name,
		description: s.description,
		shortDescription: s.shortDescription,
		price: s.price,
		image: s.image,
	}));

	const theme = useTheme();
	const [activeStep, setActiveStep] = useState(0);
	const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
	const [translatedServices, setTranslatedServices] =
		useState<ServiceDescription[]>(mappedServices);
	const [loadingTranslations, setLoadingTranslations] = useState(false);

	const locale = useContext(LangContext)!.locale;

	useEffect(() => {
		const translateServices = async () => {
			let translated;
			if (locale !== "es-ES") {
				translated = await generateTranslations(
					mappedServices,
					locale,
					setLoadingTranslations
				);
			} else {
				const storedServicesStr = localStorage.getItem("services");
				const storedServices = JSON.parse(storedServicesStr!);
				translated = storedServices["es-ES"];
			}
			setTranslatedServices(
				translated.filter((s) =>
					filteredServices.find((ms) => ms.id === s.id)
				)
			);
		};

		translateServices();
	}, [locale, services]);

	const maxSteps = filteredServices.length;

	const handleBack = () => {
		setActiveStep((prevActiveStep) =>
			prevActiveStep === 0 ? maxSteps - 1 : prevActiveStep - 1
		);
		setExpandedIndex(null);
	};

	const handleNext = () => {
		setActiveStep((prevActiveStep) =>
			prevActiveStep === maxSteps - 1 ? 0 : prevActiveStep + 1
		);
		setExpandedIndex(null);
	};

	const handleStepChange = (step: number) => {
		setActiveStep(step);
		setExpandedIndex(null);
	};

	const handleChooseService = useCallback(
		(service: ServiceDescription) => {
			const foundService = filteredServices.find((s) => s.id === service.id)!;
			onChangeService(foundService);
			onNext();
		},
		[onChangeService, onNext]
	);

	const handleToggleExpand = useCallback((index: number) => {
		setExpandedIndex((prevIndex) => (prevIndex === index ? null : index));
	}, []);

	return (
		<Box
			sx={{
				borderRadius: "7px",
				height: "auto",
				width: { mobile: "95%", xs: "80%", sm: "100%" },
			}}
		>
			{loadingTranslations ? (
				<LoadingTranslations services={filteredServices} />
			) : (
				<>
					<AutoPlaySwipeableViews
						axis={theme.direction === "rtl" ? "x-reverse" : "x"}
						index={activeStep}
						onChangeIndex={handleStepChange}
						enableMouseEvents
						autoplay={filteredServices.length > 1 && expandedIndex === null}
						interval={3000}
						style={{ height: "auto" }}
					>
						{translatedServices.map((service, index) => (
							<CarouselPaper
								service={service}
								key={index}
								index={index}
								activeStep={activeStep}
								onChooseService={handleChooseService}
								expanded={expandedIndex === index}
								onToggleExpand={() => handleToggleExpand(index)}
							/>
						))}
					</AutoPlaySwipeableViews>
					<MobileStepper
						steps={maxSteps}
						position="static"
						activeStep={activeStep}
						sx={{ mt: -1 }}
						backButton={
							<Button size="small" onClick={handleBack}>
								{theme.direction === "rtl" ? (
									<KeyboardArrowRight fontSize="small" />
								) : (
									<KeyboardArrowLeft fontSize="small" />
								)}
							</Button>
						}
						nextButton={
							<Button size="small" onClick={handleNext}>
								{theme.direction === "rtl" ? (
									<KeyboardArrowLeft fontSize="small" />
								) : (
									<KeyboardArrowRight fontSize="small" />
								)}
							</Button>
						}
					/>
				</>
			)}
		</Box>
	);
};
